import { useEffect, useRef, useState } from 'react';
import { IModule } from 'redux-state/layout/types.js';
import { IModuleConfig, ISlotConfig } from 'redux-state/layout/types.modules.js';
import { IMarker } from 'types/markerCore/marker-core-type.js';
import { shallowCompareSortedArr } from 'utilities/array.js';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';
import { predicateString } from 'utilities/isType.js';

export const actionHasModules = (action: IMarker['action']): action is ISlotConfig<'Extra'> => {
  if (predicateString(action)) {
    return false;
  }
  return !!action?.modules;
};

const isModuleConfigArrSame = (
  configNewArr: IModuleConfig[] | undefined = [],
  configPrevArr: IModuleConfig[] | undefined = [],
) => {
  if (configNewArr.length === configPrevArr.length) {
    if (configNewArr.every((configNew, i) => isModuleConfigSame(configNew, configPrevArr?.[i]))) {
      return true;
    }
  }
  return false;
};

/** We need to check if the config is the same so we need to check if the type of module is the same
 * If it is then we need to check if the properties are the same.
 * As we dont know what specific properties are in the module we need to check the key
 */
const isModuleConfigSame = (configNew: IModuleConfig | undefined, configOld: IModuleConfig | undefined) => {
  if (configNew && !configNew.key) {
    messageLog.error('Important: Module config does not have a key, this may cause bugs', ['configData', configNew]);
  }
  const { name: name1, title: title1, key: key1, link: link1 } = configNew ?? {};
  const { name: name2, title: title2, key: key2, link: link2 } = configOld ?? {};
  const keys1 = Object.keys(configNew ?? {});
  const keys2 = Object.keys(configOld ?? {});
  const item1 = [name1, title1, key1, link1?.url, ...keys1];
  const item2 = [name2, title2, key2, link2?.url, ...keys2];
  return shallowCompareSortedArr(item1, item2);
};

/**
 * Memoise the module config so we do not get constant refreshing of the component
 * Checks to see if the modules are different if so update the config reference
 * So the component reloads */
export const ModuleConfigDiff = <T extends IModuleConfig>({
  config = [],
}: {
  config: IModule<T>['config'][];
}): IModule<T>['config'][] => {
  const prevModuleConfigArr = useRef(config);
  const [newConfig, setNewConfig] = useState(config);

  useEffect(() => {
    if (!isModuleConfigArrSame(config, prevModuleConfigArr.current)) {
      setNewConfig(config);
      prevModuleConfigArr.current = config;
    }
  }, [config]);

  return newConfig;
};
